import { useContext, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { toast } from 'hooks/useToaster';
import { getLaneHistory } from 'lib/api/getLaneHistory';
import {
  SuperDispatchInternalLaneContextStatus,
  getQuickQuote,
  getSuperDispatchInternalLaneContext,
} from 'lib/api/getQuickQuote';
import { Email } from 'types/Email';
import { TransportType } from 'types/QuoteRequest';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { Quoting } from 'types/enums/Integrations';
import { captureResponseInPosthog } from 'utils/captureResponseInPosthog';

import {
  SUPER_DISPATCH_VEHICLE_CATALOG_LOAD_ERROR_MESSAGE,
  canonicalizeAutoTransportCatalogNames,
  loadSuperDispatchVehicleCatalog,
} from '../FTL/superDispatchVehicleCatalog';
import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { QuickQuoteInputs, QuoteCountries, SelectedQuoteType } from '../types';
import { fetchQuoteNumber } from '../utils/api';
import { sendGreenscreensQuote } from '../utils/api';
import { normalizeStopsForQuickQuoteSubmission } from '../utils/normalizeStopsForQuickQuoteSubmission';
import {
  handleQuoteLaneHistory,
  parseQuoteCardsFromResponse,
  sortQuoteCardsForDisplay,
} from '../utils/quoteCards';
import {
  validateAutoTransport,
  validateStopsAllowQuoteLocationFormats,
} from '../utils/validation';
import { useSuperDispatchLaneContextPoll } from './useSuperDispatchLaneContextPoll';

// Quote error source mapping for error messages
const quoteErrorSourceMap: Record<Quoting, string> = {
  [Quoting.DAT]: 'DAT',
  [Quoting.KencoFlash]: 'Kenco Flash',
  [Quoting.Greenscreens]: 'Triumph', // GS is rebranding to Triumph
  [Quoting.GlobalTranz]: 'GlobalTranz',
  [Quoting.TruckStop]: 'TruckStop',
  [Quoting.Tai]: 'Tai',
  [Quoting.Sonar]: 'SONAR',
  [Quoting.Transfix]: 'Transfix',
  [Quoting.SuperDispatch]: 'Super Dispatch',
  [Quoting.KitCoach]: 'Kit Coach',
  [Quoting.TruPay]: 'TruPay',
};

export const useQuoteSubmission = (
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>,
  email: Maybe<Email>
) => {
  const posthog = usePostHog();
  const {
    currentState: { clickedSuggestion, threadId, tabId },
  } = useContext(SidebarStateContext);
  const { serviceID } = useServiceFeatures();

  const {
    serviceFeaturesEnabled: {
      isMultiStopQuickQuoteEnabled,
      isQuoteSubmissionViaURLEnabled,
      isQuoteLaneHistoryEnabled,
      isTMSLaneHistoryEnabled,
      isQuoteSubmissionToServiceEnabled,
    },
  } = useServiceFeatures();

  const context = useQuickQuoteContext();
  const {
    setIsSubmitToTMS,
    setCreatedQuoteId,
    setCreatedQuoteInternalId,
    setQuoteResponse,
    setQuoteCards,
    setQuoteCardErrors,
    setCarrierCost,
    setLaneHistory,
    setDATFuelSurcharge,
    setHasThirdPartyQuoteURLs,
    setQuoteNotConfident,
    setIsLoadingLaneHistory,
    setParentQuoteRequestId,
    setGreenscreensQuoteID,
    setProfit,
    profitType,
    quoteMode,
  } = context;

  const { setValue, setError, control } = formMethods;
  const transportType = useWatch({ control, name: 'transportType' });
  const {
    cancelPoll: cancelSuperDispatchPoll,
    isGenerationCurrent: isSuperDispatchPollCurrent,
    startPoll: startSuperDispatchPoll,
  } = useSuperDispatchLaneContextPoll({ transportType, setQuoteCards });

  // Lets a slow submit tell whether a newer one has started.
  const latestSubmitIdRef = useRef(0);

  /**
   * Main form submission handler for getting a quick quote
   */
  const submitQuote = async (
    formValues: QuickQuoteInputs,
    selectedQuickQuoteId?: number
  ): Promise<void> => {
    const lastIndex = formValues.stops.length - 1;

    // Keep the previous cards and polls when validation fails.
    const emptyStops = formValues.stops.filter((stop) => !stop.location);
    if (emptyStops.length > 0) {
      toast({
        description:
          'Please enter locations for all stops before getting a quote.',
        variant: 'destructive',
      });
      return;
    }

    const pickupDate = new Date(formValues.pickupDate);

    // Validate pickup date exists
    if (!pickupDate) {
      toast({
        description: 'Please enter a pickup date.',
        variant: 'destructive',
      });
      return;
    }

    // Validate and parse all stops
    const validationErrors = validateStopsAllowQuoteLocationFormats(
      formValues,
      setError
    );
    if (validationErrors.length > 0) {
      return;
    }

    let updatedFormValues: QuickQuoteInputs = {
      ...formValues,
      stops: normalizeStopsForQuickQuoteSubmission(formValues.stops),
    };

    if (updatedFormValues.transportType === TransportType.AUTO) {
      try {
        await loadSuperDispatchVehicleCatalog();
      } catch {
        toast({
          description: SUPER_DISPATCH_VEHICLE_CATALOG_LOAD_ERROR_MESSAGE,
          variant: 'destructive',
        });
        return;
      }

      if (updatedFormValues.autoTransport) {
        updatedFormValues = {
          ...updatedFormValues,
          autoTransport: canonicalizeAutoTransportCatalogNames(
            updatedFormValues.autoTransport
          ),
        };
      }

      const autoTransportErrors = validateAutoTransport(
        updatedFormValues,
        setError
      );
      if (autoTransportErrors.length > 0) {
        return;
      }
    }

    latestSubmitIdRef.current += 1;
    const submitId = latestSubmitIdRef.current;
    const pollGeneration = cancelSuperDispatchPoll();
    setIsSubmitToTMS(true);
    setCreatedQuoteId(null);
    setCreatedQuoteInternalId(null);
    setQuoteResponse(null);
    setQuoteCards([]);
    setQuoteCardErrors([]);
    setCarrierCost(0);
    setLaneHistory(null);
    setIsLoadingLaneHistory(false);
    setDATFuelSurcharge(null);

    // Fetch quote number when submitting quotes via URL is supported
    if (isQuoteSubmissionViaURLEnabled) {
      fetchQuoteNumber({
        email,
        setHasThirdPartyQuoteURLs,
        setValue,
      });
    }

    // Get the quick quote
    let tabURL: Undef<string>;
    if (tabId && tabId > 0) {
      try {
        const tab = await chrome.tabs.get(tabId);
        tabURL = tab.url;
      } catch {
        // Tab may be closed; omit portal source context.
      }
    }

    const newQuote = await getQuickQuote({
      isMultiStopQuickQuoteEnabled,
      email,
      threadId,
      clickedSuggestion,
      formValues: updatedFormValues,
      formMethods,
      setQuoteNotConfidentHandler: setQuoteNotConfident,
      setProfit,
      profitType,
      quoteMode,
      tabURL,
      ...(selectedQuickQuoteId && { selectedQuickQuoteId }),
    });

    let tabHost: Undef<string>;
    try {
      tabHost = tabURL ? new URL(tabURL).hostname : undefined;
    } catch {
      tabHost = undefined;
    }

    captureResponseInPosthog(posthog, !newQuote, {
      serviceID,
      submissionType: ButtonNamePosthog.GetQuickQuote,
      tabContext: {
        tabId: tabId || undefined,
        tabHost,
        tabURL,
      },
    });

    // Return early if no quote response
    if (!newQuote) return;

    // The rep may have submitted again or left auto transport while this
    // request ran. Drop the stale response so it can't replace newer cards.
    if (!isSuperDispatchPollCurrent(pollGeneration)) {
      return;
    }

    // Set the parent quote request ID for later updates
    setParentQuoteRequestId(newQuote.quoteRequestId || 0);

    // Set quote details
    setQuoteResponse(newQuote);

    const willFetchLaneHistory =
      isQuoteLaneHistoryEnabled || isTMSLaneHistoryEnabled;
    if (willFetchLaneHistory) {
      setIsLoadingLaneHistory(true);
    }

    // Build cards from GetQuickQuote response (includes Trident/Olympus DAT when enabled)
    const quickQuoteCards = newQuote.quotes?.length
      ? parseQuoteCardsFromResponse({
          newQuote,
          formValues: updatedFormValues,
          setDATFuelSurcharge,
        })
      : [];

    if (quickQuoteCards.length) {
      setQuoteCards(sortQuoteCardsForDisplay(quickQuoteCards));
    }

    const superDispatchCard = quickQuoteCards.find(
      (card) =>
        card.type === SelectedQuoteType.SUPER_DISPATCH &&
        card.superDispatchMetadata?.internalLaneContextStatus ===
          SuperDispatchInternalLaneContextStatus.Pending
    );
    if (superDispatchCard) {
      startSuperDispatchPoll({
        generation: pollGeneration,
        quoteId: superDispatchCard.quoteId,
        fetchLaneContext: (signal) =>
          getSuperDispatchInternalLaneContext({
            isMultiStopQuickQuoteEnabled,
            email,
            threadId,
            clickedSuggestion,
            formValues: updatedFormValues,
            quoteMode,
            quoteRequestId: newQuote.quoteRequestId,
            signal,
          }),
      });
    }

    // Deduplicate and set quote card errors
    const deduplicatedQuoteCardErrors = [
      ...new Set(
        newQuote.quoteErrors?.map(
          (error) =>
            `${quoteErrorSourceMap[error.source] ?? error.source} - ${error.error}`
        )
      ),
    ];
    setQuoteCardErrors(deduplicatedQuoteCardErrors);

    // Handle submit-to-service logic (Greenscreens)
    if (isQuoteSubmissionToServiceEnabled && newQuote.quotes?.length) {
      const greenscreensQuotes = newQuote.quotes.filter(
        (q) => q.source === Quoting.Greenscreens
      );
      if (greenscreensQuotes.length > 0) {
        await sendGreenscreensQuote({
          quote: {
            ...newQuote,
            quotes: greenscreensQuotes,
          },
          setGreenscreensQuoteID,
        });
      }
    }

    // Fetch lane history
    // TODO: Multi-stop lane history ENG-3759
    let laneHistoryCards: any[] = [];
    if (willFetchLaneHistory) {
      const res = await getLaneHistory({
        quoteRequestId: newQuote?.quoteRequestId || 0,
        originCity: updatedFormValues.stops[0].city,
        originState: updatedFormValues.stops[0].state,
        originZip: updatedFormValues.stops[0].zip,
        originCountry: updatedFormValues.stops[0].country as QuoteCountries,
        destinationCity: updatedFormValues.stops[lastIndex].city,
        destinationState: updatedFormValues.stops[lastIndex].state,
        destinationZip: updatedFormValues.stops[lastIndex].zip,
        destinationCountry: updatedFormValues.stops[lastIndex]
          .country as QuoteCountries,
        transportType: updatedFormValues.transportType,
        vehicleCount:
          updatedFormValues.transportType === TransportType.AUTO
            ? updatedFormValues.autoTransport?.vehicles?.length || undefined
            : undefined,
      });

      // A newer submit owns the lane history state and spinner now. Leaving
      // auto transport doesn't start one, so this submit still clears its own.
      if (submitId !== latestSubmitIdRef.current) {
        return;
      }

      if (res.isOk()) {
        setLaneHistory(res.value);
        laneHistoryCards = handleQuoteLaneHistory(
          res.value,
          updatedFormValues.stops
        );
      }

      setIsLoadingLaneHistory(false);
    }

    // Add lane history quote cards
    if (laneHistoryCards.length) {
      setQuoteCards((prev) => [...prev, ...laneHistoryCards]);
    }
  };

  return {
    submitQuote,
    cancelSuperDispatchPoll,
  };
};
